import { useMemo, useState } from 'react';
import {
  CAMPAIGN_TREND_BUCKET_OPTIONS,
  formatCampaignTrendAxisLabel,
  formatCampaignTrendTooltip,
  getCampaignTrendUnitLabel,
  type CampaignTrendBucketSelection,
  type CampaignTrendPoint,
  type CampaignTrendSeries,
} from '../../lib/campaignEngagementTrend';

type CampaignClickTrendChartProps = {
  series: CampaignTrendSeries;
  bucketSelection: CampaignTrendBucketSelection;
  onBucketSelectionChange: (selection: CampaignTrendBucketSelection) => void;
  className?: string;
};

const CHART_WIDTH = 640;
const CHART_HEIGHT = 200;
const MARGIN = { top: 32, right: 40, bottom: 32, left: 36 };
const PLOT_W = CHART_WIDTH - MARGIN.left - MARGIN.right;
const PLOT_H = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

type LineCoords = { x: number; y: number }[];

function buildLinePath(coords: LineCoords): string {
  return coords
    .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(' ');
}

function niceTickMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  if (normalized <= 1) return magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 5) return 5 * magnitude;
  return 10 * magnitude;
}

function formatAxisCount(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10_000 ? 0 : 1)}k`;
  return String(Math.round(value));
}

function pickTickIndices(length: number, maxTicks = 6): number[] {
  if (length <= 0) return [];
  if (length <= maxTicks) return Array.from({ length }, (_, i) => i);
  const indices: number[] = [];
  for (let i = 0; i < maxTicks; i += 1) {
    indices.push(Math.round((i / (maxTicks - 1)) * (length - 1)));
  }
  return [...new Set(indices)];
}

function buildSeries(
  points: CampaignTrendPoint[],
  maxActivity: number,
  maxSent: number,
) {
  if (points.length === 0) {
    return {
      sent: [] as LineCoords,
      opens: [] as LineCoords,
      clicks: [] as LineCoords,
      sentPath: '',
      opensPath: '',
      clicksPath: '',
      maxActivity: 1,
      maxSent: 1,
    };
  }

  const toCoords = (value: (point: CampaignTrendPoint) => number, maxY: number): LineCoords =>
    points.map((p, i) => {
      const x = points.length <= 1 ? PLOT_W / 2 : (i / (points.length - 1)) * PLOT_W;
      const y = PLOT_H - (value(p) / maxY) * PLOT_H;
      return { x: x + MARGIN.left, y: y + MARGIN.top };
    });

  const sent = toCoords((p) => p.sentCount, maxSent);
  const opens = toCoords((p) => p.openCount, maxActivity);
  const clicks = toCoords((p) => p.clickCount, maxActivity);

  return {
    sent,
    opens,
    clicks,
    sentPath: buildLinePath(sent),
    opensPath: buildLinePath(opens),
    clicksPath: buildLinePath(clicks),
    maxActivity,
    maxSent,
  };
}

export default function CampaignClickTrendChart({
  series,
  bucketSelection,
  onBucketSelectionChange,
  className = '',
}: CampaignClickTrendChartProps) {
  const { points, bucketMode } = series;
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const maxActivity = useMemo(
    () =>
      niceTickMax(
        Math.max(
          1,
          ...points.flatMap((p) => [p.openCount, p.clickCount]),
        ),
      ),
    [points],
  );
  const maxSent = useMemo(
    () => niceTickMax(Math.max(1, ...points.map((p) => p.sentCount))),
    [points],
  );

  const chartSeries = useMemo(
    () => buildSeries(points, maxActivity, maxSent),
    [points, maxActivity, maxSent],
  );

  const tickIndices = useMemo(() => pickTickIndices(points.length), [points.length]);
  const unitLabel = getCampaignTrendUnitLabel(bucketMode);
  const hovered = hoverIndex != null ? points[hoverIndex] : null;
  const hoverX = hoverIndex != null ? chartSeries.sent[hoverIndex]?.x : null;
  const hoverXPercent = hoverX != null ? (hoverX / CHART_WIDTH) * 100 : null;

  const activityGridYs = [0, 0.25, 0.5, 0.75, 1].map(
    (pct) => MARGIN.top + PLOT_H * (1 - pct),
  );

  const pickNearestIndex = (clientX: number, rect: DOMRect) => {
    if (points.length === 0) return null;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const xInViewBox = ratio * CHART_WIDTH;
    let nearest = 0;
    let minDist = Infinity;
    chartSeries.sent.forEach((c, i) => {
      const dist = Math.abs(c.x - xInViewBox);
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    return nearest;
  };

  if (points.length === 0) {
    return (
      <div
        className={`flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 ${className}`}
      >
        No activity in this window yet
      </div>
    );
  }

  return (
    <div className={`relative w-full min-w-0 min-h-[12rem] ${className}`}>
      <div className="absolute top-1 left-2 right-2 z-20 flex flex-wrap items-start justify-between gap-2 pointer-events-none">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-0.5 bg-gray-500 rounded" />
            Sent
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-0.5 bg-sky-500 rounded" />
            Opens
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-0.5 bg-violet-500 rounded" />
            Clicks
          </span>
          <span className="normal-case text-gray-400 dark:text-gray-500">· per {unitLabel}</span>
        </div>
        <label className="pointer-events-auto inline-flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
          <span className="uppercase tracking-wide font-medium">Bracket</span>
          <select
            value={bucketSelection}
            onChange={(e) =>
              onBucketSelectionChange(e.target.value as CampaignTrendBucketSelection)
            }
            className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1.5 py-0.5 text-[10px] font-medium normal-case"
            aria-label="Time bracket for engagement chart"
          >
            <option value="auto">Auto</option>
            {CAMPAIGN_TREND_BUCKET_OPTIONS.map((opt) => (
              <option key={opt.mode} value={opt.mode}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="none"
        className="w-full h-full block cursor-crosshair"
        role="img"
        aria-label="Sends, opens, and clicks per time bucket since first send"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          setHoverIndex(pickNearestIndex(e.clientX, rect));
        }}
        onMouseLeave={() => setHoverIndex(null)}
      >
        {activityGridYs.map((y, i) => (
          <line
            key={`grid-h-${i}`}
            x1={MARGIN.left}
            y1={y}
            x2={MARGIN.left + PLOT_W}
            y2={y}
            stroke="rgb(203 213 225)"
            strokeWidth="1"
            strokeOpacity={i === 0 ? 0.9 : 0.45}
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {tickIndices.map((idx) => {
          const x = chartSeries.sent[idx]?.x ?? MARGIN.left;
          return (
            <line
              key={`grid-v-${points[idx]?.periodStart ?? idx}`}
              x1={x}
              y1={MARGIN.top}
              x2={x}
              y2={MARGIN.top + PLOT_H}
              stroke="rgb(203 213 225)"
              strokeWidth="1"
              strokeOpacity={0.35}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}

        <path
          d={chartSeries.sentPath}
          fill="none"
          stroke="rgb(107 114 128)"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={chartSeries.opensPath}
          fill="none"
          stroke="rgb(14 165 233)"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={chartSeries.clicksPath}
          fill="none"
          stroke="rgb(139 92 246)"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {hoverIndex != null && hoverX != null ? (
          <>
            <line
              x1={hoverX}
              y1={MARGIN.top}
              x2={hoverX}
              y2={MARGIN.top + PLOT_H}
              stroke="rgb(148 163 184)"
              strokeWidth="1"
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
            {[
              { y: chartSeries.sent[hoverIndex]?.y, fill: 'rgb(107 114 128)' },
              { y: chartSeries.opens[hoverIndex]?.y, fill: 'rgb(14 165 233)' },
              { y: chartSeries.clicks[hoverIndex]?.y, fill: 'rgb(139 92 246)' },
            ].map((dot, idx) => (
              <circle
                key={`hover-dot-${idx}`}
                cx={hoverX}
                cy={dot.y ?? MARGIN.top + PLOT_H}
                r="3.5"
                fill={dot.fill}
                stroke="white"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </>
        ) : null}

        {[0, maxActivity].map((value, i) => {
          const y = MARGIN.top + PLOT_H * (1 - value / maxActivity);
          return (
            <text
              key={`y-left-${i}`}
              x={MARGIN.left - 4}
              y={y + 3}
              textAnchor="end"
              fontSize="9"
              fill="rgb(100 116 139)"
            >
              {formatAxisCount(value)}
            </text>
          );
        })}

        {[0, maxSent].map((value, i) => {
          const y = MARGIN.top + PLOT_H * (1 - value / maxSent);
          return (
            <text
              key={`y-right-${i}`}
              x={MARGIN.left + PLOT_W + 4}
              y={y + 3}
              textAnchor="start"
              fontSize="9"
              fill="rgb(107 114 128)"
            >
              {formatAxisCount(value)}
            </text>
          );
        })}

        {tickIndices.map((idx) => {
          const x = chartSeries.sent[idx]?.x ?? MARGIN.left;
          const label = formatCampaignTrendAxisLabel(points[idx]?.periodStart ?? '', bucketMode);
          return (
            <text
              key={`x-${points[idx]?.periodStart ?? idx}`}
              x={x}
              y={CHART_HEIGHT - 8}
              textAnchor="middle"
              fontSize="9"
              fill="rgb(100 116 139)"
            >
              {label}
            </text>
          );
        })}
      </svg>

      <div className="absolute left-0 top-[calc(50%+8px)] -translate-y-1/2 -rotate-90 origin-center text-[8px] font-medium uppercase tracking-wide text-sky-600/80 dark:text-sky-400/80 pointer-events-none">
        Opens / clicks
      </div>
      <div className="absolute right-0 top-[calc(50%+8px)] -translate-y-1/2 rotate-90 origin-center text-[8px] font-medium uppercase tracking-wide text-gray-500/80 pointer-events-none">
        Sent
      </div>

      {hovered && hoverXPercent != null ? (
        <div
          className="pointer-events-none absolute top-9 z-20 -translate-x-1/2 rounded-md border border-gray-200 dark:border-gray-600 bg-white/95 dark:bg-gray-800/95 px-2 py-1 shadow-sm whitespace-nowrap backdrop-blur-sm"
          style={{ left: `${hoverXPercent}%` }}
        >
          <div className="text-[10px] font-medium text-gray-900 dark:text-gray-100">
            {formatCampaignTrendTooltip(hovered.periodStart, bucketMode)}
          </div>
          <div className="text-[10px] tabular-nums text-gray-600 dark:text-gray-300">
            <span className="text-gray-700 dark:text-gray-200 font-semibold">
              {hovered.sentCount.toLocaleString()}
            </span>{' '}
            sent
            <span className="mx-1 text-gray-300">·</span>
            <span className="text-sky-600 dark:text-sky-400 font-semibold">
              {hovered.openCount.toLocaleString()}
            </span>{' '}
            opens
            <span className="mx-1 text-gray-300">·</span>
            <span className="text-violet-600 dark:text-violet-400 font-semibold">
              {hovered.clickCount.toLocaleString()}
            </span>{' '}
            clicks
          </div>
        </div>
      ) : null}
    </div>
  );
}
